import React, { useCallback, useContext, useEffect, useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { css } from '@emotion/css';
import { Separator, Text } from '../../common';
import {
  checkOnboarding,
  //resetOnboarding,
  setOnboardingDone,
} from '../../../services/onboarding';
import Gif from './assets/OnboardingLabels.gif';
import { ProgressBar } from './progress';
import { UserContext } from '../../../context';
import { trackEvent } from '../../../libs';
import { theme } from '@repo-sapiens/theme';
import { CloseIcon } from '../../icons';
import { differenceInSeconds } from 'date-fns';

export const OnboardingDialog: React.FC = () => {
  const [active, setActive] = useState(false);
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState<Date | null>();

  const { state: userState } = useContext(UserContext);

  useEffect(() => {
    //resetOnboarding();
    const check = checkOnboarding();
    setOpen(check);
    if (check) {
      const startDate = new Date();
      setStart(startDate);
    }
    const timer = setTimeout(() => {
      setActive(true);
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const handleClose = () => {
    setOpen(false);
    setOnboardingDone();
    const endDate = new Date();
    const duration = differenceInSeconds(endDate, start || new Date());
    if (userState?._id) {
      trackEvent('Onborading Completed', {
        app: 'web',
        $email: userState?.email,
        $distinct_id: userState?._id,
        duration: Math.abs(duration),
      });
    }
  };

  const handleAbort = useCallback(() => {
    setOpen(false);
    setOnboardingDone();
    const endDate = new Date();
    const duration = differenceInSeconds(endDate, start || new Date());
    if (userState?._id) {
      trackEvent('Onborading Skiped', {
        app: 'web',
        $email: userState?.email,
        $distinct_id: userState?._id,
        duration: Math.abs(duration),
      });
    }
  }, [start, userState?._id, userState?.email]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleAbort();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleAbort]);

  return (
    <AlertDialog.Root open={open}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay
          className={css`
            background-color: rgba(0, 0, 0, 0.9);
            position: fixed;
            inset: 0;
            animation: overlayShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
          `}
        />
        <AlertDialog.Content
          className={css`
            border-radius: 24px;
            border: 1px solid #29282f;
            background: rgba(22, 22, 23);
            box-shadow: 0px 40px 40px -16px rgba(0, 0, 0, 0.5),
              0px 0px 32px 0px rgba(57, 61, 68, 0.2) inset;

            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 90vw;
            height: 90vh;
            max-width: 1189px;
            max-height: 709px;
            animation: contentShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
            z-index: 1001;
            padding: 38px;
            @media (max-height: 700px) {
              padding: 12px;
            }
            @media (max-width: 700px) {
              padding: 12px 8px;
            }
            @media (max-width: 1000px) {
              max-height: none;
            }
          `}
        >
          <div
            className={css`
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: row;
              post: relative;
              align-items: flex-end;
              @media (max-width: 1000px) {
                flex-direction: column;
                align-items: center;
              }
            `}
          >
            <AlertDialog.Action
              asChild
              className={css`
                position: absolute;
                right: 38px;
                top: 38px;
                cursor: pointer;
                @media (max-width: 700px) {
                  right: 12px;
                  top: 12px;
                }
              `}
            >
              <button onClick={handleAbort}>
                <CloseIcon
                  fill="#4E4C5B"
                  style={{ width: '32px', height: '32px' }}
                />
              </button>
            </AlertDialog.Action>
            <div
              className={css`
                width: 63%;
                height: 100%;
                border-radius: 8px;
                border: 1px solid ${theme.colors.border.primary};
                overflow: hidden;
                display: flex;
                justify-content: center;
                align-items: center;
                background-image: url(${Gif});
                background-size: cover;
                @media (max-width: 1000px) {
                  width: 100%;
                }
              `}
            />

            <div
              className={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-end;
                width: 36.5%;
                padding: 0px 32px;
                @media (max-height: 700px) {
                  padding: 0px 12px;
                }
                @media (max-width: 1000px) {
                  width: 100%;
                }
                @media (max-width: 700px) {
                  padding: 0px 6px;
                }
              `}
            >
              <ProgressBar />

              <Text.H1>Maximizing productivity!</Text.H1>
              <Text.Body color="seconday">
                Prioritizing emails... Drafting responses... We're already
                working in the background to make you more productive!
              </Text.Body>
              <Separator.Horizontal spacing={6} />
              <AlertDialog.Action asChild>
                <button
                  className={css`
                    border-radius: 76px;
                    border: 1.333px solid #2e2d33;
                    display: flex;
                    width: 100%;
                    padding: 20.5px 0px;
                    justify-content: center;
                    align-items: center;
                    flex-direction: column;
                    cursor: ${active ? 'pointer' : 'not-allowed'};
                    color: ${active ? '#0D0D0D' : '#2E2D33'};
                    background-color: ${active ? '#AAEF65' : 'transparent'};

                    transition: background-color 0.3s ease-in-out;
                  `}
                  disabled={!active}
                  onClick={handleClose}
                >
                  Got it
                </button>
              </AlertDialog.Action>
            </div>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};
